import { Box, Button, Modal } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CloseModalIcon } from "../../../../common/components/assets/commonassets/CommonAssets";
import DatePickerField from "../../../../common/components/FormFields/DatePickerFieldNew";
import InputField from "../../../../common/components/FormFields/InputField";
import SearchDropdown from "../../../../common/components/FormFields/searchDropdown";
import { ModalStyle } from "../../../../common/components/ModalStyle";
import {
    AssetMakeSearchDropdown,
    BatchNoSearchDropdown,
    ItemNameSearchDropdown,
    LocationSearchDropdown,
    ModelNoSearchDropdown,
    UpdateDataItemInfoList,
    UpdateListing,
} from "../../../services/iteminfoservices/Iteminfolistservices";

export default function UpdateDialog({
  updateDialogOpen,
  onClose,
  id,
  setSnack,
  onUpdateSuccess,
}) {
  const [searchBarString, setSearchBarString] = useState("");
  const [ItemNameOptions, setItemNameOptions] = useState([]);
  const [AssetMakeOptions, setAssetMakeOptions] = useState([]);
  const [ModelNoOptions, setModelNoOptions] = useState([]);
  const [BatchNoOptions, setBatchNoOptions] = useState([]);
  const [LocationOptions, setLocationOptions] = useState([]);
  const [assetMakeSearch, setAssetMakeSearch] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);

  const defaultFormValues = {
    itemName: null,
    assetMakeName: null,
    modelNo: null,
    batchNo: null,
    locationName: null,
    serialNo: "",
    installationDate: new Date(),
    commissionedDate: new Date(),
  };

  const { control, watch, setValue, reset } = useForm({
    defaultValues: defaultFormValues,
  });

  const handleUpdate = async () => {
    try {
      const values = watch();

      const payload = {
        id: id,
        itemId: values.itemName?.id || null,
        assetMakeId: values.assetMakeName?.id || null,
        batchNo: values.batchNo?.value || "",
        modelNo: values.modelNo?.value || "",
        serialNo: values.serialNo || "",
        locationId: values.locationName?.id || null,
        installationDate: values.installationDate
          ? dayjs(values.installationDate).toISOString()
          : null,
        commissionedDate: values.commissionedDate
          ? dayjs(values.commissionedDate).toISOString()
          : null,
      };

      console.log("UPDATE PAYLOAD 👉", payload);

      // ✅ API CALL
      const res = await UpdateDataItemInfoList(payload);

      onUpdateSuccess({
        id,
        itemName: values.itemName?.value || "",
        assetMake: values.assetMakeName?.value || "",
        batchNo: values.batchNo?.value || "",
        modelNo: values.modelNo?.value || "",
        serialNo: values.serialNo || "",
        location: values.locationName?.value || "",
      });

      setSnack({
        open: true,
        message: res?.data?.message || "Item updated successfully",
        severity: "success",
      });

      onClose();
    } catch (error) {
      console.error("Update failed", error);
      setSnack({
        open: true,
        message: error?.response?.data?.message || "Failed to update item",
        severity: "error",
      });
    }
  };

  const handleItemName = (inputValue) => {
    if (typeof inputValue !== "string") return;

    setSearchBarString(inputValue);

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setItemNameOptions([]);
      return;
    }

    ItemNameSearchDropdown(trimmed)
      .then((res) => {
        setItemNameOptions(res?.data?.result ?? []);
      })
      .catch(() => {
        setItemNameOptions([]);
      });
  };

  const handleBatchNo = (inputValue) => {
    if (typeof inputValue !== "string") return;

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setBatchNoOptions([]);
      return;
    }

    BatchNoSearchDropdown(trimmed)
      .then((res) => {
        const result = (res?.data?.result ?? []).map((item, index) => ({
          ...item,
          id: item.id ?? index,
          label: item.value,
        }));

        setBatchNoOptions(result);
      })
      .catch(() => {
        setBatchNoOptions([]);
      });
  };

  const handleModelNo = (inputValue) => {
    if (typeof inputValue !== "string") return;

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setModelNoOptions([]);
      return;
    }

    ModelNoSearchDropdown(trimmed)
      .then((res) => {
        const result = (res?.data?.result ?? []).map((item, index) => ({
          ...item,
          id: item.id ?? index,
          label: item.value,
        }));

        setModelNoOptions(result);
      })
      .catch(() => {
        setModelNoOptions([]);
      });
  };

  const handleAssetMake = (inputValue) => {
    if (typeof inputValue !== "string") return;

    setSearchBarString(inputValue);

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setAssetMakeOptions([]);
      return;
    }

    AssetMakeSearchDropdown(trimmed)
      .then((res) => {
        setAssetMakeOptions(res?.data?.result ?? []);
      })
      .catch(() => {
        setAssetMakeOptions([]);
      });
  };

  const handleLocation = (inputValue) => {
    if (typeof inputValue !== "string") return;

    setSearchBarString(inputValue);

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setLocationOptions([]);
      return;
    }

    LocationSearchDropdown(trimmed)
      .then((res) => {
        setLocationOptions(res?.data?.result ?? []);
      })
      .catch(() => {
        setLocationOptions([]);
      });
  };

  const resetAllData = () => {
    reset(defaultFormValues);
  };

  useEffect(() => {
    if (updateDialogOpen) {
      setIsEditMode(false);
    }
  }, [updateDialogOpen]);

  useEffect(() => {
    if (!updateDialogOpen || !id) return;

    const fetchData = async () => {
      const res = await UpdateListing(id);
      const data = res?.data?.result?.[0];
      if (!data) return;

      let itemOpt = null;
      if (data["Item Name"]) {
        const r = await ItemNameSearchDropdown(data["Item Name"]);
        itemOpt = r?.data?.result?.[0] || null;
        setItemNameOptions(itemOpt ? [itemOpt] : []);
      }

      let assetOpt = null;
      if (data["Asset Make"]) {
        const r = await AssetMakeSearchDropdown(data["Asset Make"]);
        assetOpt = r?.data?.result?.[0] || null;
        setAssetMakeOptions(assetOpt ? [assetOpt] : []);
      }

      let locationOpt = null;
      if (data["Location"]) {
        const r = await LocationSearchDropdown(data["Location"]);
        locationOpt = r?.data?.result?.[0] || null;
        setLocationOptions(locationOpt ? [locationOpt] : []);
      }

      const batchOpt = data["Batch No"]
        ? { value: data["Batch No"], label: data["Batch No"] }
        : null;

      const modelOpt = data["Model No"]
        ? { value: data["Model No"], label: data["Model No"] }
        : null;

      setBatchNoOptions(batchOpt ? [batchOpt] : []);
      setModelNoOptions(modelOpt ? [modelOpt] : []);

      reset({
        itemName: itemOpt,
        assetMakeName: assetOpt,
        batchNo: batchOpt,
        modelNo: modelOpt,
        locationName: locationOpt,
        serialNo: data["Serial No"] ?? "",
        installationDate: data["Installation Date"]
          ? new Date(data["Installation Date"])
          : null,
        commissionedDate: data["Commissioned Date"]
          ? new Date(data["Commissioned Date"])
          : null,
      });
    };

    fetchData();
  }, [updateDialogOpen, id, reset]);

  return (
    <Modal open={updateDialogOpen}>
      <Box
        sx={ModalStyle}
        className="w-[65%] p-4 space-y-2 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex justify-between">
          <label className="font-semibold">
            <span>Update Item Details</span>
          </label>
          <CloseModalIcon onClick={onClose} />
        </div>

        <div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <SearchDropdown
              control={control}
              name="itemName"
              placeholder="Search Item Name"
              menuPlacement="bottom"
              dataArray={ItemNameOptions}
              inputValue={searchBarString}
              isClearable
              searchIcon
              handleInputChange={handleItemName}
              getOptionLabel={(option) => option?.value || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />

            <SearchDropdown
              control={control}
              name="assetMakeName"
              placeholder="Asset Make"
              menuPlacement="bottom"
              dataArray={AssetMakeOptions}
              inputValue={searchBarString}
              isClearable
              handleInputChange={handleAssetMake}
              getOptionLabel={(option) => option?.value || ""}
              isOptionEqualToValue={(option, value) =>
                option?.id
                  ? option.id === value?.id
                  : option?.value === value?.value
              }
            />

            <SearchDropdown
              control={control}
              name="modelNo"
              placeholder="Model No"
              menuPlacement="bottom"
              dataArray={ModelNoOptions}
              inputValue={searchBarString}
              isClearable
              handleInputChange={handleModelNo}
              getOptionLabel={(option) => option?.value || ""}
              isOptionEqualToValue={(option, value) =>
                option?.id
                  ? option.id === value?.id
                  : option?.value === value?.value
              }
            />

            <SearchDropdown
              control={control}
              name="batchNo"
              placeholder="Search Batch No"
              menuPlacement="bottom"
              dataArray={BatchNoOptions}
              isClearable
              searchIcon
              handleInputChange={handleBatchNo}
              getOptionLabel={(option) => option?.value || ""}
              isOptionEqualToValue={(option, value) =>
                option?.id
                  ? option.id === value?.id
                  : option?.value === value?.value
              }
            />

            <SearchDropdown
              control={control}
              name="locationName"
              placeholder="Location"
              menuPlacement="bottom"
              dataArray={LocationOptions}
              inputValue={searchBarString}
              isClearable
              handleInputChange={handleLocation}
              getOptionLabel={(option) => option?.value || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />

            <DatePickerField
              control={control}
              name="installationDate"
              label="Installation Date"
              inputFormat={"dd-MM-yyyy"}
              slotProps={{ textField: { size: "small" } }}
              disabled
            />

            <DatePickerField
              control={control}
              name="commissionedDate"
              label="Commissioned Date"
              inputFormat={"dd-MM-yyyy"}
              slotProps={{textField:{size:"small"}}}
              disabled
            />

            <InputField
              control={control}
              name="serialNo"
              label="Serial No"
              placeholder="Serial No"
            />
          </div>

          <div className="flex items-center justify-end gap-2  mt-6">
            <button
              type="button"
              onClick={resetAllData}
              className="h-[36px] px-4 rounded border border-red-500 text-red-500 text-sm font-semibold hover:bg-red-50"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={() => setConfirmUpdateOpen(true)}
              className="h-[36px] px-4 rounded text-white bg-blue-900 hover:bg-blue-800 text-sm font-semibold"
            >
              Update
            </button>
          </div>
        </div>

        <Modal open={confirmUpdateOpen}>
          <Box
            sx={{
              width: 400,
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 2,
              mx: "auto",
              mt: "20%",
            }}
          >
            <h3 className="font-semibold text-lg mb-3">Confirm Update</h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to update this item?
            </p>

            <div className="flex justify-end gap-2">
              <Button
                color="success"
                onClick={() => setConfirmUpdateOpen(false)}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setConfirmUpdateOpen(false);
                  handleUpdate();
                }}
              >
                Confirm
              </Button>
            </div>
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
}
